import { collection, addDoc, getDocs, doc, updateDoc, query, where, orderBy, limit, startAfter, increment, Timestamp, writeBatch, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Post } from '../types/firebase';
import { User } from 'firebase/auth';
import { addPostReaction, removePostReaction } from './postReactionService';

export const createPost = async (
  content: string,
  causes: string[],
  primaryCause: string,
  user: User
): Promise<string> => {
  try {
    const postData = {
      content: content.trim(),
      causes,
      primaryCause,
      userId: user.uid,
      userName: user.displayName || 'Anonymous',
      userAvatar: user.photoURL || '',
      date: Timestamp.now(),
      lastUpdated: Timestamp.now(),
      reactions: {
        thumbsUp: { count: 0, users: [] },
        thumbsDown: { count: 0, users: [] },
        love: { count: 0, users: [] },
        laugh: { count: 0, users: [] },
        wow: { count: 0, users: [] },
        cry: { count: 0, users: [] },
        vomit: { count: 0, users: [] },
        angry: { count: 0, users: [] },
        poop: { count: 0, users: [] },
        clown: { count: 0, users: [] }
      },
      commentCount: 0,
      type: 'post'
    };

    const docRef = await addDoc(collection(db, 'posts'), postData);
    
    // Update user stats
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      'stats.postsCreated': increment(1)
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

export const getPosts = async (
  filters: {
    cause?: string;
  } = {},
  sort: {
    field: 'date' | 'commentCount';
    direction: 'asc' | 'desc';
  } = { field: 'date', direction: 'desc' },
  lastVisible?: any,
  pageSize: number = 10
): Promise<{ posts: Post[]; lastVisible: any }> => {
  try {
    let q = query(collection(db, 'posts'), orderBy(sort.field, sort.direction), limit(pageSize));
    
    if (filters.cause) {
      q = query(collection(db, 'posts'), where('causes', 'array-contains', filters.cause), orderBy(sort.field, sort.direction), limit(pageSize));
    }
    
    if (lastVisible) {
      q = query(collection(db, 'posts'), orderBy(sort.field, sort.direction), startAfter(lastVisible), limit(pageSize));
    }
    
    const snapshot = await getDocs(q);
    const posts = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as Post[];
    
    return {
      posts,
      lastVisible: snapshot.docs[snapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
};

export const getPost = async (postId: string): Promise<Post | null> => {
  try {
    const docSnap = await getDocs(query(collection(db, 'posts'), where('__name__', '==', postId)));
    
    if (!docSnap.empty) {
      const postData = docSnap.docs[0].data();
      return {
        ...postData,
        id: docSnap.docs[0].id
      } as Post;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching post:', error);
    throw error;
  }
};

export const deletePost = async (postId: string, userId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const post = postDoc.data() as Post;
    
    // Check if user is authorized to delete the post
    if (post.userId !== userId) {
      // Check if user is admin
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
      
      if (!isAdmin) {
        throw new Error('Unauthorized to delete this post');
      }
    }
    
    const batch = writeBatch(db);
    
    // Delete all comments for this post (including all levels of replies)
    const commentsQuery = query(collection(db, 'posts', postId, 'comments'));
    const commentsSnapshot = await getDocs(commentsQuery);
    commentsSnapshot.docs.forEach(commentDoc => {
      batch.delete(commentDoc.ref);
    });
    
    // Delete all reactions for this post
    const reactionsQuery = query(collection(db, 'posts', postId, 'reactions'));
    const reactionsSnapshot = await getDocs(reactionsQuery);
    reactionsSnapshot.docs.forEach(reactionDoc => {
      batch.delete(reactionDoc.ref);
    });
    
    // Delete the post document itself
    batch.delete(postRef);
    
    // Update user stats
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      'stats.postsCreated': increment(-1),
      lastActive: Timestamp.now()
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

export const updatePostReactions = async (
  postId: string,
  reactionType: keyof Post['reactions'],
  userId: string,
  action: 'add' | 'remove'
): Promise<void> => {
  try {
    if (action === 'add') {
      await addPostReaction(postId, userId, reactionType);
    } else {
      await removePostReaction(postId, userId, reactionType);
    }
  } catch (error) {
    console.error('Error updating post reactions:', error);
    throw error;
  }
};

export const incrementPostCommentCount = async (postId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentCount: increment(1)
    });
  } catch (error) {
    console.error('Error incrementing post comment count:', error);
    throw error;
  }
};

export const decrementPostCommentCount = async (postId: string): Promise<void> => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      commentCount: increment(-1)
    });
  } catch (error) {
    console.error('Error decrementing post comment count:', error);
    throw error;
  }
}; 